import { useEffect, useState, useCallback, useRef } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, FileText, ClipboardList, Upload, Trash2, Send, ExternalLink, ShieldAlert, CalendarClock, MessageCircleQuestion } from "lucide-react";

const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};
const money = (n) => (n || n === 0 ? `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : "—");
const PROP_PILL = {
  submitted: ["Under review", "bg-amber-100 text-amber-800"], approved: ["Approved", "bg-emerald-50 text-emerald-700"],
  declined: ["Not selected", "bg-red-50 text-red-700"], withdrawn: ["Withdrawn", "bg-sand-100 text-ink/45"],
};

function ProposalForm({ token, project, onDone, onCancel }) {
  const [f, setF] = useState({ scope: "", pricing_type: "fixed", amount: "", line_items: [{ desc: "", amount: "" }], start_date: "", duration: "", submitted_by: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!f.scope.trim()) return toast.error("Describe the scope of work");
    setBusy(true);
    try {
      await api.post(`/fs/vendor/${token}/proposals`, {
        project_id: project.id, title: project.title, scope: f.scope,
        pricing_type: f.pricing_type,
        amount: f.pricing_type === "fixed" ? f.amount : null,
        line_items: f.pricing_type === "line_items" ? f.line_items.filter((l) => l.desc || l.amount) : [],
        start_date: f.start_date, duration: f.duration, submitted_by: f.submitted_by,
      });
      toast.success("Proposal submitted — the community has been notified");
      onDone?.();
    } catch (e) { toast.error(errMsg(e, "Could not submit")); }
    setBusy(false);
  };

  return (
    <div className="mt-3 space-y-2.5 rounded-xl border border-redline-200 bg-white p-3" data-testid={`proposal-form-${project.id}`}>
      <textarea data-testid="pf-scope" value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} rows={3}
        placeholder="Scope of work — what you'll do"
        className="w-full rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
      <div className="grid grid-cols-2 gap-2">
        {[["fixed", "One price"], ["line_items", "Line items"]].map(([k, l]) => (
          <button key={k} data-testid={`pf-pricing-${k}`} onClick={() => setF({ ...f, pricing_type: k })}
            className={`rounded-lg px-3 py-2 text-sm font-bold ${f.pricing_type === k ? "bg-ink text-white" : "bg-sand-100 text-ink/60"}`}>{l}</button>
        ))}
      </div>
      {f.pricing_type === "fixed" ? (
        <input data-testid="pf-amount" value={f.amount} inputMode="decimal" onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Total price ($)"
          className="w-full rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
      ) : (
        <div className="space-y-1.5">
          {f.line_items.map((li, i) => (
            <div key={i} className="flex gap-1.5">
              <input data-testid={`pf-li-desc-${i}`} value={li.desc} onChange={(e) => { const items = [...f.line_items]; items[i] = { ...li, desc: e.target.value }; setF({ ...f, line_items: items }); }}
                placeholder="Item" className="min-w-0 flex-1 rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
              <input data-testid={`pf-li-amt-${i}`} value={li.amount} inputMode="decimal" onChange={(e) => { const items = [...f.line_items]; items[i] = { ...li, amount: e.target.value }; setF({ ...f, line_items: items }); }}
                placeholder="$" className="w-24 rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
              <button onClick={() => setF({ ...f, line_items: f.line_items.filter((_, j) => j !== i) })} className="rounded-lg p-2 text-ink/30 hover:text-redline-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <button data-testid="pf-add-li" onClick={() => setF({ ...f, line_items: [...f.line_items, { desc: "", amount: "" }] })}
            className="inline-flex items-center gap-1 rounded-lg border border-dashed border-sand-300 px-2.5 py-1 text-xs font-bold text-ink/55"><Plus className="h-3 w-3" /> Add line</button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="pf-start" type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })}
          className="rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
        <input data-testid="pf-duration" value={f.duration} onChange={(e) => setF({ ...f, duration: e.target.value })} placeholder="Duration (e.g. 3 days)"
          className="rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
      </div>
      <input data-testid="pf-by" value={f.submitted_by} onChange={(e) => setF({ ...f, submitted_by: e.target.value })} placeholder="Your name (optional)"
        className="w-full rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
      <div className="flex gap-2">
        <button data-testid={`pf-submit-${project.id}`} onClick={submit} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-xl bg-redline-500 px-4 py-2 text-sm font-bold text-white hover:bg-redline-600 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit proposal
        </button>
        <button onClick={onCancel} className="rounded-xl border border-sand-300 px-3 py-2 text-sm font-semibold text-ink/60">Cancel</button>
      </div>
    </div>
  );
}

const PAPER = { coi: "your certificate of insurance", w9: "your W-9", agreement: "a signed agreement" };

/* Coming out to look at the job needs NO paperwork — that's deliberate. Paperwork is what buys
   the right to put a price in. Asking a question is free too. */
function BidExtras({ token, p, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [dates, setDates] = useState("");
  const [q, setQ] = useState("");
  const sv = p.my_site_visit;
  const qs = p.questions || [];

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); onChanged?.(); }
    catch (e) { toast.error(errMsg(e, "Try again")); }
    setBusy(false);
  };

  return (
    <div className="mt-3 space-y-3 border-t border-sand-100 pt-3" data-testid={`vp-extras-${p.id}`}>
      {p.paperwork && !p.paperwork.ok && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5"
          data-testid={`vp-gate-${p.id}`}>
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="min-w-0 text-xs leading-relaxed text-amber-900">
            {p.paperwork_blocks_bid ? (
              <>
                You can look at this job and come out to see it. Before you can put a <b>price</b> in we
                need {(p.paperwork.missing || []).map((m) => PAPER[m.key] || m.label).join(" and ")} —
                upload it higher up this page and come straight back.
              </>
            ) : (
              <>
                <b>Send your price now</b> — you don't need your paperwork in first. Before you can
                start the work we'll need {(p.paperwork.missing || []).map((m) => PAPER[m.key] || m.label).join(" and ")};
                upload it higher up this page whenever you get a minute.
              </>
            )}
          </div>
        </div>
      )}

      <div>
        <div className="mb-1 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-ink/40">
          <CalendarClock className="h-3.5 w-3.5" /> Come and look at it
        </div>
        {sv ? (
          <div className="rounded-xl bg-sand-50 px-3 py-2 text-xs" data-testid={`vp-sv-state-${p.id}`}>
            {sv.status === "confirmed" || sv.status === "done" ? (
              <span className="font-semibold text-emerald-700">
                Confirmed: {sv.window}{sv.access_note ? ` · ${sv.access_note}` : ""}
              </span>
            ) : sv.status === "declined" ? (
              <span className="text-ink/55">They couldn't do a walk-through{sv.decline_reason ? ` — ${sv.decline_reason}` : ""}. You can still bid from the scope.</span>
            ) : (
              <span className="text-ink/55">Asked for {(sv.proposed || []).join(" · ")} — waiting on the manager.</span>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            <input data-testid={`vp-sv-dates-${p.id}`} value={dates} onChange={(e) => setDates(e.target.value)}
              placeholder="Times that suit you, e.g. Tue am, Wed pm"
              className="min-w-0 flex-1 rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
            <button data-testid={`vp-sv-request-${p.id}`} disabled={busy || dates.trim().length < 3}
              onClick={() => run(async () => {
                await api.post(`/fs/vendor/${token}/projects/${p.id}/site-visit`,
                  { dates: dates.split(",").map((x) => x.trim()).filter(Boolean) });
                setDates("");
              }, "Asked — the manager will confirm a time")}
              className="rounded-lg bg-ink px-3 py-2 text-sm font-bold text-white disabled:opacity-40">Ask</button>
          </div>
        )}
        <p className="mt-1 text-[11px] text-ink/40">No paperwork needed to walk the property.</p>
      </div>

      <div>
        <div className="mb-1 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-ink/40">
          <MessageCircleQuestion className="h-3.5 w-3.5" /> Questions
        </div>
        {qs.map((x) => (
          <div key={x.id} className="mb-1.5 rounded-xl bg-sand-50 px-3 py-2 text-xs" data-testid={`vp-q-${x.id}`}>
            <div className="font-semibold text-ink">{x.q}{x.mine ? " (yours)" : ""}</div>
            <div className="mt-0.5 text-ink/60">{x.a || "Not answered yet."}</div>
          </div>
        ))}
        {!p.bids_closed && (
          <div className="flex flex-wrap gap-1.5">
            <input data-testid={`vp-q-input-${p.id}`} value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Ask about the job — everyone bidding sees the answer"
              className="min-w-0 flex-1 rounded-lg border border-sand-300 px-3 py-2 text-sm outline-none" />
            <button data-testid={`vp-q-send-${p.id}`} disabled={busy || q.trim().length < 5}
              onClick={() => run(async () => {
                await api.post(`/fs/vendor/${token}/projects/${p.id}/question`, { q });
                setQ("");
              }, "Sent — you'll get the answer by text or email")}
              className="rounded-lg bg-ink px-3 py-2 text-sm font-bold text-white disabled:opacity-40">Send</button>
          </div>
        )}
      </div>
    </div>
  );
}

function MyProposal({ token, p, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pill = PROP_PILL[p.status] || PROP_PILL.submitted;
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/fs/vendor/${token}/proposals/${p.id}/attachment`, fd); toast.success("Attached"); onChanged?.(); }
    catch (er) { toast.error(errMsg(er, "Upload failed")); }
    setBusy(false);
  };
  return (
    <div data-testid={`my-proposal-${p.id}`} className="rounded-xl bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-bold text-ink">{p.title}</div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${pill[1]}`}>{pill[0]}</span>
          <span className="text-sm font-extrabold text-ink">{money(p.total)}</span>
        </div>
      </div>
      {p.scope && <p className="mt-1 line-clamp-2 text-xs text-ink/55">{p.scope}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {(p.attachments || []).map((a, i) => (
          <a key={i} href={a.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-sand-300 px-2 py-1 text-xs font-semibold text-ink/60">
            <FileText className="h-3 w-3" /> {a.name || "file"} <ExternalLink className="h-3 w-3" />
          </a>
        ))}
        {p.status === "submitted" && (
          <>
            <button data-testid={`my-proposal-attach-${p.id}`} onClick={() => fileRef.current?.click()} disabled={busy}
              className="inline-flex items-center gap-1 rounded-lg border border-dashed border-sand-300 px-2 py-1 text-xs font-bold text-ink/55">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Attach a file
            </button>
            <input ref={fileRef} type="file" accept="application/pdf,image/*" onChange={upload} className="hidden" />
          </>
        )}
      </div>
    </div>
  );
}

export default function VendorProposals({ token }) {
  const [d, setD] = useState(null);
  const [formFor, setFormFor] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/fs/vendor/${token}/projects`); setD(data); }
    catch { setD({ projects: [], proposals: [] }); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (!d) return null;
  const openProjects = d.projects || [];
  const mine = d.proposals || [];
  if (openProjects.length === 0 && mine.length === 0) return null;

  return (
    <div className="mt-6" data-testid="vendor-proposals">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">
        <ClipboardList className="h-3.5 w-3.5" /> Proposals & bids
      </div>
      {openProjects.map((p) => (
        <div key={p.id} data-testid={`vp-project-${p.id}`} className="mb-2 rounded-2xl bg-white p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="text-sm font-bold text-ink">{p.title}</div>
                {p.new_community && (
                  <span data-testid={`vp-new-community-${p.id}`} className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-700">New community</span>
                )}
                {p.bids_closed && (
                  <span data-testid={`vp-closed-${p.id}`} className="rounded-full bg-sand-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ink/60">Bids closed</span>
                )}
                {p.scope_revision > 1 && (
                  <span data-testid={`vp-scope-rev-${p.id}`} className="rounded-full bg-sand-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-ink/60">Scope v{p.scope_revision}</span>
                )}
              </div>
              {(mine || []).some((m) => m.project_id === p.id && m.status === "reprice") && !p.already_proposed && (
                <div data-testid={`vp-reprice-${p.id}`} className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900">
                  The scope changed after your proposal (now v{p.scope_revision}). Your earlier price was set aside — read the new scope and submit again.
                </div>
              )}
              <div className="mt-0.5 text-xs text-ink/45">
                {p.community} · {p.mode === "rfp" ? "Open for bids" : "Requested from you"}{p.category ? ` · ${CAT_LABEL[p.category] || p.category}` : ""}
                {p.bids_due ? ` · due ${p.bids_due.slice(0, 10)}` : ""}
              </div>
              {(p.scope || (p.scope_files || []).length > 0) && (
                <details className="mt-2" data-testid={`vp-scope-${p.id}`}>
                  <summary className="cursor-pointer text-xs font-bold text-redline-600">
                    Read the scope of work{(p.scope_files || []).length ? ` · ${p.scope_files.length} document${p.scope_files.length === 1 ? "" : "s"}` : ""}
                    {p.scope_updated_at ? ` · updated ${p.scope_updated_at.slice(0, 10)}` : ""}
                  </summary>
                  {p.scope && (
                    <pre data-testid={`vp-scope-text-${p.id}`} className="mt-2 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-xl bg-sand-50 p-3 font-sans text-xs leading-relaxed text-ink/75">{p.scope}</pre>
                  )}
                  {(p.scope_files || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {p.scope_files.map((sf) => (
                        <a key={sf.id} href={sf.url} target="_blank" rel="noreferrer" data-testid={`vp-scope-file-${sf.id}`}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-sand-300 px-2.5 py-1 text-xs font-semibold text-ink/70 hover:border-ink/30">
                          <FileText className="h-3.5 w-3.5" /> {sf.name}
                        </a>
                      ))}
                    </div>
                  )}
                </details>
              )}
            </div>
            {p.already_proposed ? (
              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">Submitted</span>
            ) : p.bids_closed ? (
              <span className="text-xs font-semibold text-ink/40">Closed {p.bids_due?.slice(0, 10)}</span>
            ) : formFor !== p.id ? (
              <button data-testid={`vp-open-form-${p.id}`} onClick={() => setFormFor(p.id)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-redline-500 px-3.5 py-2 text-sm font-bold text-white hover:bg-redline-600">
                <Plus className="h-4 w-4" /> Submit a proposal
              </button>
            ) : null}
          </div>
          {p.description && <p className="mt-1 text-sm text-ink/60">{p.description}</p>}
          {formFor === p.id && (
            <ProposalForm token={token} project={p} onCancel={() => setFormFor(null)}
              onDone={() => { setFormFor(null); load(); }} />
          )}
          {!p.already_proposed && <BidExtras token={token} p={p} onChanged={load} />}
        </div>
      ))}
      {mine.length > 0 && (
        <div className="mt-3">
          <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/40">Your proposals</div>
          <div className="space-y-2">
            {mine.map((p) => <MyProposal key={p.id} token={token} p={p} onChanged={load} />)}
          </div>
        </div>
      )}
    </div>
  );
}
